import { HashRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import Layout from "@/components/layout/Layout";
import { AuthProvider, useAuth } from "@/components/providers/AuthProvider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { InitialDataProvider } from "@/components/providers/InitialDataProvider";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { supabase } from "@/lib/supabase";
import { useEffect, useState } from "react";

// Pages
import Login from "@/pages/Login";
import AdminLogin from "@/pages/AdminLogin";
import Dashboard from "@/pages/Dashboard";
import UserDashboard from "@/pages/UserDashboard";
import Products from "@/pages/Products";
import ProductForm from "@/pages/ProductForm";
import Categories from "@/pages/Categories";
import Stock from "@/pages/Stock";
import Movements from "@/pages/Movements";
import Orders from "@/pages/Orders";
import UserOrders from "@/pages/UserOrders";
import CreateOrder from "@/pages/CreateOrder";
import OrdersManagement from "@/pages/OrdersManagement";
import Cadastro from "@/pages/Cadastro";
import Inventory from "@/pages/Inventory";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import Suppliers from "@/pages/Suppliers";
import UserSettings from "@/pages/user/UserSettings";
import MyOrders from "@/pages/MyOrders";
import ProductSearch from "@/pages/ProductSearch";
import AdminOrderDetails from "@/pages/AdminOrderDetails";
import UserOrderDetails from "@/pages/UserOrderDetails";
import OrderDetails from "@/pages/OrderDetails";

const queryClient = new QueryClient();

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

const ProtectedRoute = ({ children, requireAdmin = false }: ProtectedRouteProps) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return <div>Carregando...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/user-dashboard" replace />;
  }

  if (!requireAdmin && isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <InitialDataProvider>
            <HashRouter>
              <Routes>
                {/* Rotas públicas */}
                <Route path="/login" element={<Login />} />
                <Route path="/admin" element={<AdminLogin />} />

                {/* Rotas do administrador */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute requireAdmin>
                      <Layout>
                        <Dashboard />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/products"
                  element={
                    <ProtectedRoute requireAdmin>
                      <Layout>
                        <Products />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/products/new"
                  element={
                    <ProtectedRoute requireAdmin>
                      <Layout>
                        <ProductForm />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/products/edit/:id"
                  element={
                    <ProtectedRoute requireAdmin>
                      <Layout>
                        <ProductForm />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/categories"
                  element={
                    <ProtectedRoute requireAdmin>
                      <Layout>
                        <Categories />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/stock"
                  element={
                    <ProtectedRoute requireAdmin>
                      <Layout>
                        <Stock />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/movements"
                  element={
                    <ProtectedRoute requireAdmin>
                      <Layout>
                        <Movements />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cadastro"
                  element={
                    <ProtectedRoute requireAdmin>
                      <Layout>
                        <Cadastro />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/inventory"
                  element={
                    <ProtectedRoute requireAdmin>
                      <Layout>
                        <Inventory />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <ProtectedRoute requireAdmin>
                      <Layout>
                        <Reports />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/orders/management"
                  element={
                    <ProtectedRoute requireAdmin>
                      <Layout>
                        <OrdersManagement />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/orders/details/:id"
                  element={
                    <ProtectedRoute requireAdmin>
                      <Layout>
                        <OrderDetails />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/orders/:orderId"
                  element={
                    <ProtectedRoute requireAdmin>
                      <Layout>
                        <AdminOrderDetails />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/orders/:id"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <UserOrderDetails />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/suppliers"
                  element={
                    <ProtectedRoute requireAdmin>
                      <Layout>
                        <Suppliers />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute requireAdmin>
                      <Layout>
                        <Settings />
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                {/* Rotas protegidas do usuário */}
                <Route
                  path="/user-dashboard"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <UserDashboard />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/my-orders"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <MyOrders />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/products/search"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <ProductSearch />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/user/settings"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <UserSettings />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/user/orders"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <UserOrders />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/orders"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Orders />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/orders/new"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <CreateOrder />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/orders/edit/:id"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <CreateOrder />
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                {/* Rota padrão */}
                <Route
                  path="/"
                  element={
                    <Navigate to="/login" replace />
                  }
                />
              </Routes>
            </HashRouter>
          </InitialDataProvider>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
